import * as Sentry from "@sentry/react";
import React, { ReactElement } from "react";
import {
  getWithExpiry,
  isChunkFailedError,
  setWithExpiry,
} from "web-core/utils";
import { ErrorComponent } from "web-core/components";
import { router } from "@/App";

interface Props {
  children: ReactElement;
}

class ErrorBoundary extends React.Component<
  Props,
  { hasError: boolean; error?: Error }
> {
  reset: null | ReturnType<typeof router.subscribe>;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.reset = null;
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidMount(): void {
    this.reset = router.subscribe("onResolved", () => {
      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    });
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    const isChunkFailed = error?.message && isChunkFailedError(error);
    const reloadAttemptedRecently = !!getWithExpiry("chunk_failed");

    if (isChunkFailed && !reloadAttemptedRecently) {
      setWithExpiry("chunk_failed", true, 60_000);
      window.location.reload();
      return;
    }

    this.setState({ hasError: true });
    // eslint-disable-next-line
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorComponent
          onError={(e) => Sentry.captureException(e)}
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
